import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import {
    Box,
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatSuperscriptIcon from '@mui/icons-material/Superscript';
import FormatSubscriptIcon from '@mui/icons-material/Subscript';

const fontSizeOptions = [
    { value: '8px', label: 'Tiny' },
    { value: '12px', label: 'Small' },
    { value: '16px', label: 'Normal' },
    { value: '20px', label: 'Large' },
    { value: '24px', label: 'X-Large' },
    { value: '32px', label: 'XX-Large' },
];

const tiptapExtensions = [
    StarterKit,
    Underline,
    TextStyle,
    Strike,
    Superscript,
    Subscript,
    FontSize.configure({ types: ['textStyle'] }),
    Link.configure({ openOnClick: true, linkOnPaste: true }),
    TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
    }),
];

// Shared toolbar — always renders, buttons disabled when no editor is active
const SharedMenuBar = ({ editor, readOnly }: { editor: Editor | null; readOnly: boolean }) => {
    const addLink = () => {
        if (!editor) return;
        const url = window.prompt('URL');
        if (url) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
    };

    const disabled = !editor || readOnly;

    return (
        <Stack
            direction="row"
            spacing={0.5}
            sx={{
                padding: '6px 8px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
                alignItems: 'center',
                flexShrink: 0,
                backgroundColor: 'background.paper',
            }}
        >
            <Tooltip title="Bold">
                <span>
                    <Button variant={editor?.isActive('bold') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleBold().run()}>
                        <FormatBoldIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Italic">
                <span>
                    <Button variant={editor?.isActive('italic') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleItalic().run()}>
                        <FormatItalicIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Underline">
                <span>
                    <Button variant={editor?.isActive('underline') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleUnderline().run()}>
                        <FormatUnderlinedIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Strikethrough">
                <span>
                    <Button variant={editor?.isActive('strike') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleStrike().run()}>
                        <FormatStrikethroughIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Superscript">
                <span>
                    <Button variant={editor?.isActive('superscript') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleSuperscript().run()}>
                        <FormatSuperscriptIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Subscript">
                <span>
                    <Button variant={editor?.isActive('subscript') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleSubscript().run()}>
                        <FormatSubscriptIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Bullet List">
                <span>
                    <Button variant={editor?.isActive('bulletList') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleBulletList().run()}>
                        <FormatListBulletedIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Numbered List">
                <span>
                    <Button variant={editor?.isActive('orderedList') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
                        <FormatListNumberedIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Align Left">
                <span>
                    <Button variant={editor?.isActive({ textAlign: 'left' }) ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().setTextAlign('left').run()}>
                        <FormatAlignLeftIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Align Center">
                <span>
                    <Button variant={editor?.isActive({ textAlign: 'center' }) ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().setTextAlign('center').run()}>
                        <FormatAlignCenterIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Align Right">
                <span>
                    <Button variant={editor?.isActive({ textAlign: 'right' }) ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={() => editor?.chain().focus().setTextAlign('right').run()}>
                        <FormatAlignRightIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Add Link">
                <span>
                    <Button variant={editor?.isActive('link') ? 'contained' : 'outlined'} size="small" disabled={disabled}
                        onClick={addLink}>
                        <InsertLinkIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            <FormControl size="small" sx={{ minWidth: 100 }} disabled={disabled}>
                <InputLabel id="shared-font-size-label">Font Size</InputLabel>
                <Select
                    labelId="shared-font-size-label"
                    value={editor?.getAttributes('textStyle').fontSize || '16px'}
                    label="Font Size"
                    onChange={(e) => {
                        editor?.chain().focus().setMark('textStyle', { fontSize: e.target.value }).run();
                    }}
                    size="small"
                    sx={{
                        '& .MuiSelect-select': { fontSize: '0.875rem', padding: '4px 8px' },
                    }}
                >
                    {fontSizeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Undo">
                <span>
                    <Button variant="outlined" size="small" disabled={disabled || !editor?.can().undo()}
                        onClick={() => editor?.chain().focus().undo().run()}>
                        <UndoIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Redo">
                <span>
                    <Button variant="outlined" size="small" disabled={disabled || !editor?.can().redo()}
                        onClick={() => editor?.chain().focus().redo().run()}>
                        <RedoIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
        </Stack>
    );
};

interface EventInfoEditorsProps {
    generalInfo: string;
    firstTeeInfo: string;
    onGeneralChange: (html: string) => void;
    onFirstTeeChange: (html: string) => void;
    readOnly: boolean;
}

export const EventInfoEditors = ({
    generalInfo,
    firstTeeInfo,
    onGeneralChange,
    onFirstTeeChange,
    readOnly,
}: EventInfoEditorsProps) => {
    // 'general' | 'firsttee' — tracks which editor was last focused
    const [activePane, setActivePane] = useState<'general' | 'firsttee'>('general');

    const generalEditor = useEditor({
        extensions: tiptapExtensions,
        content: generalInfo,
        editable: !readOnly,
        onFocus: () => setActivePane('general'),
        onUpdate: ({ editor }) => onGeneralChange(editor.getHTML()),
    });

    const firstTeeEditor = useEditor({
        extensions: tiptapExtensions,
        content: firstTeeInfo,
        editable: !readOnly,
        onFocus: () => setActivePane('firsttee'),
        onUpdate: ({ editor }) => onFirstTeeChange(editor.getHTML()),
    });

    // Sync editable state when readOnly changes
    useEffect(() => {
        generalEditor?.setEditable(!readOnly);
        firstTeeEditor?.setEditable(!readOnly);
    }, [readOnly, generalEditor, firstTeeEditor]);

    // Sync content when external value changes (e.g. loading a different event)
    useEffect(() => {
        if (generalEditor && generalInfo !== generalEditor.getHTML()) {
            generalEditor.commands.setContent(generalInfo);
        }
    }, [generalInfo, generalEditor]);

    useEffect(() => {
        if (firstTeeEditor && firstTeeInfo !== firstTeeEditor.getHTML()) {
            firstTeeEditor.commands.setContent(firstTeeInfo);
        }
    }, [firstTeeInfo, firstTeeEditor]);

    const activeEditor = activePane === 'general' ? generalEditor : firstTeeEditor;

    const editorContentSx = {
        flex: 1,
        overflow: 'auto',
        padding: '12px',
        cursor: readOnly ? 'default' : 'text',
        '& .ProseMirror': {
            outline: 'none',
            minHeight: '100%',
            textAlign: 'left',
            fontFamily: (theme: any) => theme.typography.fontFamily,
            fontSize: (theme: any) => theme.typography.fontSize,
            '& p': { margin: '0 0 0.5em 0' },
            '& ul, & ol': { margin: '0 0 0.5em 0', paddingLeft: '1.5em' },
        },
        '& .ProseMirror-focused': { outline: 'none' },
    };

    return (
        <Stack direction="column" sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
            {/* Shared toolbar */}
            <Box sx={{ flexShrink: 0 }}>
                <SharedMenuBar editor={activeEditor ?? null} readOnly={readOnly} />
            </Box>

            {/* Side-by-side editor panes */}
            <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0, pt: 1 }}>
                {/* General Information */}
                <Stack direction="column" spacing={0.5} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: activePane === 'general' ? 700 : 400,
                            color: activePane === 'general' ? 'primary.main' : 'text.secondary',
                            flexShrink: 0,
                            transition: 'color 0.15s, font-weight 0.15s',
                        }}
                    >
                        General Information
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            border: '1px solid',
                            borderColor: activePane === 'general' ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'border-color 0.15s',
                            cursor: readOnly ? 'default' : 'text',
                        }}
                        onClick={() => !readOnly && generalEditor?.commands.focus()}
                    >
                        <Box sx={editorContentSx}>
                            <EditorContent editor={generalEditor} style={{ height: '100%' }} />
                        </Box>
                    </Box>
                </Stack>

                {/* First Tee */}
                <Stack direction="column" spacing={0.5} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: activePane === 'firsttee' ? 700 : 400,
                            color: activePane === 'firsttee' ? 'primary.main' : 'text.secondary',
                            flexShrink: 0,
                            transition: 'color 0.15s, font-weight 0.15s',
                        }}
                    >
                        First Tee
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            border: '1px solid',
                            borderColor: activePane === 'firsttee' ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'border-color 0.15s',
                            cursor: readOnly ? 'default' : 'text',
                        }}
                        onClick={() => !readOnly && firstTeeEditor?.commands.focus()}
                    >
                        <Box sx={editorContentSx}>
                            <EditorContent editor={firstTeeEditor} style={{ height: '100%' }} />
                        </Box>
                    </Box>
                </Stack>
            </Stack>
        </Stack>
    );
};
